import React, { useState, useEffect } from "react";
import {
  CreditCard,
  X,
  Lock,
  ChevronRight,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// Assurez-vous de remplacer ça par votre clé publique Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY  )// || "pk_test_51PbKhNAPxByS1QClbDJXO2XRv9oaFiWtH87CqvTzsWziPqCshPfIBuTkvUcxw1SGDNwFMoRJKTsagO5VgY4iG55E00DhshQFsp");

// Composant principal du modal de paiement
const PaymentModal = ({ isOpen, onClose, quote, onPaymentSuccess, totalAmount }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <Elements stripe={stripePromise}>
          <PaymentForm
            onClose={onClose}
            quote={quote}
            onPaymentSuccess={onPaymentSuccess}
            totalAmount={totalAmount}
          />
        </Elements>
      </div>
    </div>
  );
};

// Composant de formulaire qui utilise les composants Stripe
const PaymentForm = ({ onClose, quote, onPaymentSuccess, totalAmount }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [cardName, setCardName] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  // const [total, setTotal] = useState(0);
  const total = totalAmount || 0;
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);

  // Helper function to safely parse sizes_quantities
  const parseSizesQuantities = (sizesData) => {
    if (!sizesData) return {};

    // If it's already an object and not an array, return it
    if (typeof sizesData === 'object' && !Array.isArray(sizesData)) {
      return sizesData;
    }

    // If it's a string, try to parse it as JSON
    if (typeof sizesData === 'string') {
      try {
        const parsed = JSON.parse(sizesData);
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing sizes_quantities string:', e);
      }
    }

    // If it's an array or other format, return empty object
    console.warn('sizes_quantities is not in expected format:', sizesData);
    return {};
  };

  // Récupérer les devis disponibles au chargement
  useEffect(() => {
    fetchAvailableQuotes();
  }, []);

  // Récupérer la liste des devis disponibles depuis l'API
  const fetchAvailableQuotes = async () => {
    try {
      const response = await axios.get("https://api.greenecobagsusa.com/api/payment/check-quotes");
      setAvailableQuotes(response.data.quotes);

      // Déterminer l'ID de devis à utiliser
      if (quote && quote.id) {
        // Vérifier si le devis existe dans la liste des devis disponibles
        const quoteExists = response.data.quotes.some(q => q.id === quote.id);
        if (quoteExists) {
          setSelectedQuoteId(quote.id);
        } else {
          // Utiliser le premier devis disponible confirmé comme alternative
          const confirmedQuote = response.data.quotes.find(q => q.status === "confirmed");
          if (confirmedQuote) {
            setSelectedQuoteId(confirmedQuote.id);
            setError(`Le devis #${quote.id} n'existe pas. Utilisation du devis #${confirmedQuote.id} à la place.`);
          } else {
            setError("Aucun devis confirmé n'est disponible pour le paiement.");
          }
        }
      } else if (response.data.quotes.length > 0) {
        // Utiliser le premier devis confirmé si aucun n'est spécifié
        const confirmedQuote = response.data.quotes.find(q => q.status === "confirmed");
        if (confirmedQuote) {
          setSelectedQuoteId(confirmedQuote.id);
        }
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des devis:", err);
      setError("Impossible de récupérer la liste des devis disponibles.");
    }
  };

  // Calcul du total basé sur le devis
  // Calcul du total basé sur le devis
  // Fonction calculateTotal à ajouter dans le composant PaymentForm
  // const calculateTotal = () => {
  //   if (!quote || !sizePrices || Object.keys(sizePrices).length === 0) {
  //     return 0;
  //   }

  //   let productTotal = 0;
  //   let totalQuantity = 0;

  //   // Use the helper function to safely parse sizes_quantities
  //   const parsedSizes = parseSizesQuantities(quote.sizes_quantities);

  //   if (Object.keys(parsedSizes).length > 0) {
  //     // Calculer le total des produits et la quantité totale
  //     for (const [size, quantity] of Object.entries(parsedSizes)) {
  //       // Récupérer le prix correspondant à cette taille
  //       const price = sizePrices[size] || 0;
  //       // Ajouter au total des produits
  //       productTotal += price * parseInt(quantity);
  //       // Ajouter à la quantité totale
  //       totalQuantity += parseInt(quantity);
  //     }
  //   } else if (quote.quantity && !isNaN(parseInt(quote.quantity))) {
  //     // Fallback to simple quantity if sizes_quantities is not available
  //     const qty = parseInt(quote.quantity);
  //     const avgPrice = Object.values(sizePrices).length > 0
  //       ? Object.values(sizePrices).reduce((sum, price) => sum + price, 0) / Object.values(sizePrices).length
  //       : 0;
  //     productTotal = avgPrice * qty;
  //     totalQuantity = qty;
  //   }

  //   // Vérifier si le logo est requis et si le prix est défini
  //   const shouldAddLogoPrice =
  //     quote.is_logo_required === true &&
  //     quote.price_logo_quote !== null &&
  //     quote.price_logo_quote !== undefined &&
  //     quote.price_logo_quote !== "" &&
  //     parseFloat(quote.price_logo_quote) > 0;

  //   console.log("shouldAddLogoPrice:", shouldAddLogoPrice);

  //   // Si le logo doit être ajouté, calculer le prix total du logo
  //   if (shouldAddLogoPrice) {
  //     const logoPrice = parseFloat(quote.price_logo_quote);
  //     console.log("logoPrice:", logoPrice);
  //     console.log("Final total with logo:", productTotal + logoPrice);
  //     return productTotal + logoPrice; // Add logo price once, not multiplied by quantity
  //   }

  //   // Si pas de logo ou conditions non remplies, retourner seulement le total des produits
  //   console.log("No logo added, final total:", productTotal);
  //   return productTotal;
  // };


  // Créer l'intention de paiement
  const createPaymentIntent = async () => {
    if (!selectedQuoteId) {
      setError("Aucun devis valide n'est sélectionné pour le paiement");
      return null;
    }

    try {
      const response = await axios.post(
        "https://api.greenecobagsusa.com/api/payment/create-intent",
        {
          amount: Math.round(total * 100), // Conversion en centimes (USD)
          product_name: `Commande #${selectedQuoteId}`,
          quote_id: selectedQuoteId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
        setPaymentIntentId(response.data.paymentIntentId);
        return response.data;
      } else {
        throw new Error(response.data.message || "Impossible d'initialiser le paiement");
      }
    } catch (err) {
      console.error("Erreur lors de la création de l'intention de paiement:", err);
      setError(err.response?.data?.message || "Erreur lors de l'initialisation du paiement");
      return null;
    }
  };

  // Initialiser l'intention de paiement au chargement du composant
  useEffect(() => {
    if (selectedQuoteId && total > 0) {
      createPaymentIntent();
    }
  }, [selectedQuoteId, total]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js n'est pas encore chargé
      return;
    }

    if (!cardName.trim()) {
      setError("Veuillez entrer le nom du titulaire de la carte");
      return;
    }

    setIsProcessing(true);
    setLoading(true);

    try {
      // Confirmer le paiement avec Stripe
      const cardElement = elements.getElement(CardElement);

      // Créer un payment method avec les détails de la carte
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardName,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Confirmer le paiement avec Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      // Informer notre backend que le paiement est confirmé
      const confirmResponse = await axios.post(
        "https://api.greenecobagsusa.com/api/payment/confirm",
        {
          payment_intent_id: paymentIntentId,
          quote_id: selectedQuoteId,
          payment_method_id: paymentMethod.id,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (confirmResponse.data.success) {
        setSuccess(true);
        setError("");

        // Mettre à jour le statut local du devis
        if (onPaymentSuccess) {
          onPaymentSuccess({
            ...quote,
            status: "paid"
          });
        }

        // Fermer la modal après un court délai
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(
          confirmResponse.data.message ||
          "Erreur lors de la confirmation du paiement"
        );
      }
    } catch (err) {
      console.error("Erreur dans le processus de paiement:", err);
      setError(err.message || "Erreur lors du traitement du paiement");
    } finally {
      setLoading(false);
      setIsProcessing(false);
    }
  };

  // Style pour le composant CardElement
  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#32325d',
        fontFamily: 'Arial, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  return (
    <>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <CreditCard size={20} className="mr-2 text-blue-600" />
          Paiement sécurisé {selectedQuoteId ? `(Devis #${selectedQuoteId})` : ''}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isProcessing}
        >
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="p-6">
        {error && (
          <div className="mb-4 bg-red-50 p-3 rounded-md flex items-start">
            <AlertCircle size={18} className="text-red-500 mr-2 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 p-3 rounded-md flex items-start">
            <CheckCircle size={18} className="text-green-500 mr-2 mt-0.5" />
            <p className="text-green-700 text-sm">
              Paiement effectué avec succès!
            </p>
          </div>
        )}

        <div className="mb-4">
          <div className="flex justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">
              Résumé de commande
            </h3>
            <span className="text-sm text-gray-500">
              #{selectedQuoteId || quote?.id || "N/A"}
            </span>
          </div>
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Statut</span>
              <span
                className={`text-sm font-medium ${quote?.status === "confirmed"
                  ? "text-green-600"
                  : "text-yellow-600"
                  }`}
              >
                {quote?.status === "confirmed"
                  ? "Confirmé"
                  : quote?.status || "N/A"}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Quantité</span>
              <span className="text-sm font-medium">
                {quote?.quantity || 0} unités
              </span>
            </div>

            {(() => {
              const parsedSizes = parseSizesQuantities(quote?.sizes_quantities);
              return Object.keys(parsedSizes).length > 0 ? (
                <>
                  {Object.entries(parsedSizes)
                    .filter(([size, qty]) => parseInt(qty) > 0)
                    .map(([size, qty]) => (
                      <div key={size} className="flex justify-between mb-2">
                        <span className="text-sm text-gray-600">Taille {size}</span>
                        <span className="text-sm font-medium">
                          {qty} unités
                        </span>
                      </div>
                    ))}
                </>
              ) : null;
            })()}

            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Total</span>
                <span className="text-sm font-bold text-blue-600">
                  {total.toFixed(2)} $
                </span>
              </div>
            </div>
          </div>
        </div>

        {!success && (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du titulaire de la carte
              </label>
              <input
                type="text"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="Nom Prénom"
                className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                disabled={isProcessing || !selectedQuoteId}
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Informations de carte
              </label>
              <div className="border border-gray-300 p-3 rounded-md">
                <CardElement options={cardStyle} />
              </div>
            </div>

            <div className="flex items-center justify-center mb-4">
              <Lock size={14} className="text-gray-500 mr-1" />
              <span className="text-xs text-gray-500">
                Paiement sécurisé par Stripe
              </span>
            </div>

            <button
              type="submit"
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || isProcessing || !selectedQuoteId || !stripe || !clientSecret}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Traitement en cours...
                </span>
              ) : (
                <>
                  Payer {total.toFixed(2)} $
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </>
  );
};

export default PaymentModal;